import { boot, keysOf, isChapterName } from '@dazn/bootstrap-lib';

import { normalizeRuntimeChapterName } from './normalizeRuntimeChapterName';

type ChapterName = boot.config.ChapterName;
export type CNString =
    | (string & { readonly __kind?: unique symbol })
    | ChapterName;

interface ChapterConfig {
    name: ChapterName;
    /**
     * Function to get chapter home page
     */
    homePage: string;
    linkKey?: string;
    /**
     * Function to get if chapter uses portability
     */
    usePortability: boolean;
    /**
     * Function to check if chapter uses signup country
     */
    useSignUpCountry: boolean;
    /**
     * Function for checking if chapter has language in URL
     */
    isLanguageInTheUrl: boolean;
    rewriteUrlLocale: boolean;
    rewriteUrlLocaleOverrideMap?: Record<string, boolean>;
    /**
     * Function for checking if gets country from URL
     */
    getsCountryFromUrl: boolean;
    urlIdentifiers: string[];
    canLoadIfSignedIn: boolean;
    canLoadIfSignedOut: boolean;
    allowedUserStatuses?: string[];
    restrictToVip?: boolean;
}

type AllChaptersConfig = Partial<Record<ChapterName, ChapterConfig>>;
type ChaptersUrlMap = Partial<Record<ChapterName, string[]>>;

const getChaptersUrlConfig = (rawChaptersData: AllChaptersConfig) =>
    keysOf(rawChaptersData).reduce(
        (result, chapterName) => {
            const chapter = rawChaptersData[chapterName];
            if (!chapter) return result;
            if (chapter.canLoadIfSignedIn) {
                result.canLoadIfSignedInChapters[chapter.name] =
                    chapter.urlIdentifiers;
            }
            if (chapter.canLoadIfSignedOut) {
                result.canLoadIfSignedOutChapters[chapter.name] =
                    chapter.urlIdentifiers;
            }
            return result;
        },
        {
            canLoadIfSignedInChapters: {} as ChaptersUrlMap,
            canLoadIfSignedOutChapters: {} as ChaptersUrlMap,
        },
    );

function getChapters(
    jsonChapterData: AllChaptersConfig,
    rootAuthorized: ChapterName,
    unauthorizedChapter: ChapterName,
    loginChapter: ChapterName,
    onLogoutChapter: ChapterName,
) {
    const internal = {
        rawChaptersData: jsonChapterData,
        rootAuthorized,
        unauthorizedChapter,
        loginChapter,
        onLogoutChapter,
        defaultAnonymousChapters: {} as boot.config.CountryToChapterMap,
    };

    const getChapter = (
        chapterName: string,
    ): Partial<ChapterConfig> | Record<string, never> => {
        if (!isChapterName(chapterName)) {
            return {};
        }
        const chapter = internal.rawChaptersData[chapterName];
        return chapter || {};
    };

    return {
        //
        // ComputedProperties
        //
        rewriteUrlLocale: (chapterName: CNString) =>
            getChapter(chapterName).rewriteUrlLocale || false,
        rewriteUrlLocaleOverrideMap: (chapterName: CNString) =>
            getChapter(chapterName).rewriteUrlLocaleOverrideMap,
        hasLanguageInUrl: (chapterName: CNString) =>
            getChapter(chapterName).isLanguageInTheUrl || false,
        getsCountryFromUrl: (chapterName: CNString) =>
            getChapter(chapterName).getsCountryFromUrl || false,
        usePortability: (chapterName: CNString) =>
            getChapter(chapterName).usePortability || false,
        useSignUpCountry: (chapterName: CNString) =>
            getChapter(chapterName).useSignUpCountry || false,
        homePage: (chapterName: CNString) =>
            getChapter(chapterName).homePage || chapterName,
        linkKey: (chapterName: CNString) => getChapter(chapterName).linkKey,
        chapterList: () => keysOf(internal.rawChaptersData),
        allowedUserStatuses: (chapter: CNString) =>
            getChapter(chapter).allowedUserStatuses,
        restrictToVip: (chapter: CNString) => getChapter(chapter).restrictToVip,
        //
        // Links
        //
        /**
         * Map of all authorized chapters and their urlIdentifiers
         */
        get authorizedMap() {
            return getChaptersUrlConfig(internal.rawChaptersData)
                .canLoadIfSignedInChapters;
        },
        /**
         * Map of all unauthorized chapters and their urlIdentifiers
         */
        get unauthorizedMap() {
            return getChaptersUrlConfig(internal.rawChaptersData)
                .canLoadIfSignedOutChapters;
        },
        /**
         * Map of all the chapters and their identifiers
         */
        get combinedChapterMap() {
            const { canLoadIfSignedInChapters, canLoadIfSignedOutChapters } =
                getChaptersUrlConfig(internal.rawChaptersData);
            return {
                ...canLoadIfSignedInChapters,
                ...canLoadIfSignedOutChapters,
            };
        },
        //
        // Getters
        //
        /**
         * Default authorized chapter
         */
        get rootAuthorized() {
            return internal.rootAuthorized;
        },
        getUnauthorized: (country: boot.config.NativeCountryOrMoon) =>
            internal.defaultAnonymousChapters[country] ||
            internal.unauthorizedChapter,
        /**
         * Chapter where to login
         */
        get loginChapter() {
            return internal.loginChapter;
        },
        /**
         * Chapter to load after logout
         */
        get onLogoutChapter() {
            return internal.onLogoutChapter;
        },
        //
        // Setters
        //
        /**
         * Apply Runtime Configuration
         */
        applyRuntimeConfiguration: (
            defaultAnonymousChapters: typeof internal.defaultAnonymousChapters,
        ) => {
            internal.defaultAnonymousChapters = keysOf(
                defaultAnonymousChapters,
            ).reduce<typeof internal.defaultAnonymousChapters>((acc, key) => {
                const chapterNames = defaultAnonymousChapters[key];
                if (chapterNames) {
                    acc[key] = normalizeRuntimeChapterName(chapterNames);
                }
                return acc;
            }, {});
        },
    };
}

export { getChapters, ChapterConfig, AllChaptersConfig };
